import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Android as AndroidIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import conversationService from '../services/conversationService';

const Search = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  
  // Search filters
  const [filters, setFilters] = useState({
    session_id: '',
    phone_number: '',
    date_from: null,
    date_to: null,
    message_content: '',
    q: '',
    advanced_where: '',
    limit: 50,
    offset: 0,
  });

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setSearched(true);
    
    try {
      // Prepare filters - only send non-empty values
      const searchFilters = {};
      if (filters.session_id) searchFilters.session_id = filters.session_id;
      if (filters.phone_number) searchFilters.phone_number = filters.phone_number;
      if (filters.date_from) searchFilters.date_from = filters.date_from.toISOString();
      if (filters.date_to) searchFilters.date_to = filters.date_to.toISOString();
      if (filters.message_content) searchFilters.message_content = filters.message_content;
      if (filters.q) searchFilters.q = filters.q;
      if (filters.advanced_where) searchFilters.advanced_where = filters.advanced_where;
      searchFilters.limit = filters.limit;
      searchFilters.offset = filters.offset;
      
      const data = await conversationService.searchGrouped(searchFilters);
      console.log('Search response:', data);
      console.log('Total results:', data?.total, 'Has more:', data?.has_more);
      
      // Expect the grouped format with conversations
      if (data && typeof data === 'object' && 'conversations' in data) {
        setResults(data.conversations || []);
        setTotalResults(data.total || 0);
        setHasMore(data.has_more || false);
      } else {
        // Fallback for unexpected format
        console.error('Unexpected search response format:', data);
        setResults([]);
        setTotalResults(0);
        setHasMore(false);
        setError('Invalid response format from server');
      }
    } catch (err) {
      setError('Failed to search conversations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      session_id: '',
      phone_number: '',
      date_from: null,
      date_to: null,
      message_content: '',
      q: '',
      advanced_where: '',
      limit: 50,
      offset: 0,
    });
    setResults([]);
    setSearched(false);
  };

  const handleViewConversation = (conversation) => {
    // Navigate using phone number from grouped results
    const encodedPhone = encodeURIComponent(conversation.participant_phone);
    navigate(`/conversations/${encodedPhone}`);
  };

  const handleFilterChange = (field) => (value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Search Conversations
      </Typography>

      {/* Search Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Quick search (q)"
              placeholder="Search across message, from, to, session"
              value={filters.q}
              onChange={(e) => handleFilterChange('q')(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Session ID"
              value={filters.session_id}
              onChange={(e) => handleFilterChange('session_id')(e.target.value)}
              placeholder="Enter session ID"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={filters.phone_number}
              onChange={(e) => handleFilterChange('phone_number')(e.target.value)}
              placeholder="Enter phone number"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date From"
                value={filters.date_from}
                onChange={handleFilterChange('date_from')}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date To"
                value={filters.date_to}
                onChange={handleFilterChange('date_to')}
                renderInput={(params) => <TextField {...params} fullWidth />}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Message Content"
              value={filters.message_content}
              onChange={(e) => handleFilterChange('message_content')(e.target.value)}
              placeholder="Search in message content"
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Advanced WHERE (optional)"
              value={filters.advanced_where}
              onChange={(e) => handleFilterChange('advanced_where')(e.target.value)}
              placeholder="Example: sender = 'bot' AND LOWER(message) LIKE '%inscripción%'"
              helperText="Safe subset only: words, quotes, %, (), =, <, >, !, - . , :"
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {searched && !loading && (
        <Paper>
          <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                Search Results 
                {totalResults > 0 && (
                  <Chip 
                    label={`${totalResults} total`} 
                    color="primary" 
                    size="small" 
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              {totalResults > results.length && (
                <Typography variant="body2" color="text.secondary">
                  Showing {results.length} of {totalResults} results
                </Typography>
              )}
            </Box>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Last Message</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Sessions</TableCell>
                  <TableCell>Messages</TableCell>
                  <TableCell>Has Response</TableCell>
                  <TableCell>Preview</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((conversation, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      {format(new Date(conversation.last_message), 'dd/MM/yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ fontSize: 16, mr: 1 }} />
                        <Typography variant="body2">
                          {conversation.participant_phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {conversation.session_count > 1 ? (
                        <Chip
                          label={`${conversation.session_count} sessions`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        '1 session'
                      )}
                    </TableCell>
                    <TableCell>{conversation.message_count}</TableCell>
                    <TableCell>
                      <Chip
                        label={conversation.has_response ? 'Answered' : 'Unanswered'}
                        size="small"
                        color={conversation.has_response ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {conversation.last_message_body}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View all messages from this number">
                        <IconButton
                          size="small"
                          onClick={() => handleViewConversation(conversation)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {results.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No conversations found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {(hasMore || filters.offset > 0) && (
            <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
                  handleSearch();
                }}
                disabled={filters.offset === 0}
              >
                Previous
              </Button>
              
              <Typography variant="body2" color="text.secondary">
                Page {Math.floor(filters.offset / filters.limit) + 1}
              </Typography>
              
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }));
                  handleSearch();
                }}
                disabled={!hasMore}
              >
                Next
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default Search;
